import ExcelJS from "exceljs";

/**
 * Parse a single CSV line, handling quoted fields with commas inside them.
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      fields.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current.trim());

  return fields;
}

function parseCsvText(text: string): Record<string, any>[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length === 0) return [];

  const headers = parseCSVLine(lines[0]);
  const rows: Record<string, any>[] = [];

  for (let i = 1; i < lines.length; i += 1) {
    const values = parseCSVLine(lines[i]);
    const obj: Record<string, any> = {};
    headers.forEach((h, idx) => {
      obj[h] = values[idx] ?? "";
    });
    rows.push(obj);
  }

  return rows;
}

function extractRowsFromWorksheet(
  worksheet: ExcelJS.Worksheet
): Record<string, any>[] {
  const rows: Record<string, any>[] = [];
  let headers: string[] = [];
  worksheet.eachRow((row, rowNumber) => {
    const values = (row.values as any[]).slice(1).map((v) => {
      if (v === undefined || v === null) return "";
      if (typeof v === "object" && "result" in v) return v.result ?? "";
      if (typeof v === "object" && "text" in v) return v.text ?? "";
      return v;
    });
    if (rowNumber === 1) {
      headers = values.map(String);
    } else {
      const obj: Record<string, any> = {};
      headers.forEach((h, i) => {
        obj[h] = values[i] ?? "";
      });
      rows.push(obj);
    }
  });

  return rows;
}

/**
 * Parse an uploaded fee schedule file (CSV or Excel) into row objects.
 * Returns an array of key-value objects where keys are the header names.
 */
export const parseUploadedFile = async (
  buffer: ArrayBuffer,
  fileName: string
): Promise<Record<string, any>[]> => {
  if (fileName.toLowerCase().endsWith(".csv")) {
    const text = new TextDecoder().decode(buffer);
    return parseCsvText(text);
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const worksheet = workbook.worksheets[0];

  return extractRowsFromWorksheet(worksheet);
};
